<template>
  <Particles id="tsparticles" :options="particleOptions"/>

  <div class="maintenance-container" :data-theme="theme">
    <div class="logo-wrapper">
      <img :src="logoSrc" alt="Maybets logo" class="logo" />
    </div>

    <h1 class="title">We'll be back soon</h1>

    <p class="subtitle">
      This message is to inform you about scheduled maintenance on our platforms to allow for important system updates.
      During this period, access to the platform will be temporarily unavailable.
      These updates are designed to improve the performance and reliability with the main goal of serving you better.
      Thank you for being a valued customer.
    </p>

    <p class="subtitle">
      If you have any questions or concerns, contact us at
      <a href="mailto:care@maybets.com">care@maybets.com</a>
    </p>

    <!-- Animated interlocking gears -->
    <div class="gears">
      <div class="gear large"></div>
      <div class="gear small"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const DARK_LOGO  = '/images/maybets.png'
const LIGHT_LOGO = '/images/Maybets-logo.svg'

const theme = ref('dark')
const getRootTheme = () =>
    document.documentElement.getAttribute('data-theme') || 'dark'

const onThemeChanged = () => { theme.value = getRootTheme() }

onMounted(() => {
  theme.value = getRootTheme()
  window.addEventListener('themeChanged', onThemeChanged)
})

onUnmounted(() => {
  window.removeEventListener('themeChanged', onThemeChanged)
})

const logoSrc = computed(() => (theme.value === 'dark' ? LIGHT_LOGO : DARK_LOGO))

const particleOptions = computed(() => ({
  background: { opacity: 0 },
  particles: {
    number: { value: 50 },
    size:   { value: 3 },
    move:   { speed: 1 },
    color:  { value: theme.value === 'dark' ? '#6e8f28' : '#9AC243' }
  }
}))
</script>

<style scoped>
@import '../assets/styles.css';

:global(#tsparticles) { position: fixed; inset: 0; pointer-events: none; }
:global(html, body, #app) { height: 100%; margin: 0; }
:global(body) { overflow: hidden; }

.maintenance-container {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  padding: 20px;
  gap: 20px;
}

/* Light/Dark text colors via data-attribute (no Tailwind needed) */
.maintenance-container[data-theme="light"] { color: #213547; }
.maintenance-container[data-theme="dark"]  { color: rgba(255,255,255,.92); }

.logo { max-width: 220px; height: auto; }
.logo-wrapper {
  margin-bottom: 10px;
}
.title {
  margin: 0;
}
.subtitle {
  max-width: 720px;
  margin: 0;
  opacity: 0.75;
}
/* Gears */
.gears {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}
.gear {
  border-radius: 50%;
  background: transparent;
  position: relative;
  box-sizing: border-box;
  border: 6px solid #9AC243; /* Maybets green */
}
.gear.large { width: 80px; height: 80px; animation: spin 4s linear infinite; }
.gear.small { width: 50px; height: 50px; animation: spin-reverse 4s linear infinite; }

/* Teeth */
.gear::before {
  content: '';
  position: absolute;
  inset: -10px;
  border: 6px solid #9AC243;
  border-radius: 50%;
  mask: repeating-conic-gradient(#000 0deg 15deg, transparent 15deg 30deg);
  -webkit-mask: repeating-conic-gradient(#000 0deg 15deg, transparent 15deg 30deg);
}
/* Center hole (uses page bg behind it) */
.gear::after {
  content: '';
  position: absolute;
  width: 20%; height: 20%;
  background: transparent;
  border-radius: 50%;
  top: 50%; left: 50%; transform: translate(-50%,-50%);
}

/* Spins */
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes spin-reverse { to { transform: rotate(-360deg); } }

a { color: #8faf37; }
.maintenance-container[data-theme="light"] a:hover { color: #8faf37; }
</style>